import React, { useMemo, useRef } from 'react';
import { Field, Formik, FormikProps } from 'formik';
import * as FS from '../form.styles';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import { SupportProvider } from '../../../../api';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

interface BillingProviderFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const BillingProviderForm: React.FunctionComponent<BillingProviderFormProps> = ({ claimEditorStore }) => {
    const formikRef = useRef<FormikProps<{ providerLookup: string }> | null>(null);
    const initialValues = useMemo(() => ({
        providerLookup: claimEditorStore.billingProviderId || '',
    }), [claimEditorStore.billingProviderId]);

    return (
        <Formik innerRef={formikRef as any} initialValues={initialValues} onSubmit={() => {}} enableReinitialize validateOnMount={false} validateOnChange={true} validateOnBlur={false}>
            {form => {
                return (
                <>
                {claimEditorStore.validationSignal > 0 && !claimEditorStore.billingProviderId && (
                    <FormErrorBanner messages={["Billing provider is required"]} />
                )}
                <FS.FormGrid>
                    <FS.FormField span={2}>
                        <FS.Label required>Billing Provider Lookup</FS.Label>
                        <Field
                            name="providerLookup"
                            component={AsyncSelectField}
                            placeholder="Type organization or first/last to search"
                            loadOptions={async (q: string) => {
                                const query = (q || '').trim().toLowerCase();

                                // Ensure cache is loaded once
                                if (!claimEditorStore['billingProvidersLoaded']) {
                                    await (claimEditorStore as any).preloadBillingProviders();
                                }

                                const list: SupportProvider[] = (claimEditorStore as any)['billingProvidersCache'] || [];
                                const startsWith = (val?: string) => (val ? val.toLowerCase().startsWith(query) : false);
                                const normalize = (val?: string) => (val ? val.toLowerCase() : '');

                                const filtered = list.filter((p: any) => {
                                    const first = normalize(p?.first || p?.provider?.first);
                                    const last = normalize(p?.last || p?.provider?.last);
                                    const org = normalize((p as any)?.organization || (p as any)?.provider?.organization);
                                    const npi = normalize((p as any)?.npi || (p as any)?.provider?.npi);
                                    return (
                                        startsWith(org) ||
                                        startsWith(first) ||
                                        startsWith(last) ||
                                        startsWith([first, last].filter(Boolean).join(' ')) ||
                                        startsWith([last, first].filter(Boolean).join(' ')) ||
                                        startsWith(npi)
                                    );
                                });

                                return filtered.map(p => ({
                                    label: (p as any).organization || [p.first, p.last].filter(Boolean).join(' ') || '',
                                    value: p.id as any,
                                    provider: p,
                                    info: p.npi ? ' | ' + `NPI: ${p.npi}` : undefined,
                                } as any));
                            }}
                            getDefault={async (id: string) => {
                                if (!id) return null;
                                const provider = await claimEditorStore.getProviderById(id);
                                if (!provider) return null;
                                const label = (provider as any).organization || [provider.first, provider.last].filter(Boolean).join(' ') || '';
                                return {
                                    label,
                                    value: provider.id as any,
                                    provider,
                                    info: provider.npi ? ' | ' + `NPI: ${provider.npi}` : undefined,
                                } as any;
                            }}
                            onChange={(opt: any, f: any) => {
                                const p: SupportProvider = opt?.provider;
                                if (!p) return;
                                claimEditorStore.updateBillingProviderId(String(p.id || ''));
                                f.setFieldValue('providerLookup', String(p.id || ''));
                            }}
                        />
                    </FS.FormField>
                </FS.FormGrid>
                </>
                );
            }}
        </Formik>
    );
};

export default BillingProviderForm;


